/**
 * Monorepo package discovery — enumerates every ArchLang package root and
 * space root in a tree, without configuration.
 *
 * `package-loader.ts` loads ONE package from a given root and treats nested
 * `package:` manifests as opaque boundaries it skips. That is correct for
 * loading a single package, but a real-life monorepo has many packages at
 * arbitrary depths. `discoverRoots` does the complementary job: a full
 * tree-walk (descending through everything) that reports where every package
 * and space lives, so the server, CLI, and web can switch between them.
 *
 * All IO goes through `LspIO`, so this is identical across the node FS, the
 * git-commit-tree adapter, and the browser VFS.
 */

import type { LspIO } from "../io.js";
import { parsePackageManifest } from "@archlang/engine";
import type { QualifiedName } from "@archlang/engine";

export interface DiscoveredRoot {
  /** Directory URI containing the manifest (or the tree root, for implicit). */
  readonly uri: string;
  /** Bare path relative to the tree root. "" = the tree root itself. */
  readonly path: string;
  readonly kind: "package" | "space";
  /** Manifest `package:` / `spaceName:` value. Absent for the implicit root. */
  readonly name?: QualifiedName;
  /** True for the synthesized anonymous package covering loose root files. */
  readonly implicit?: boolean;
  /**
   * Bare paths of the OTHER discovered packages this one declares as
   * `dependencies` (resolved from each dep's relative `path`). Only paths that
   * resolve to a package found in this same tree are listed — external/stdlib
   * deps are omitted. Lets callers compute a reverse-dependency closure (e.g.
   * "which packages must be re-resolved when package X changed"). Package roots
   * only; undefined for spaces.
   */
  readonly deps?: readonly string[];
}

export interface DiscoveryResult {
  /** Package roots, repo-root package (path "") first, then by path. */
  readonly packages: DiscoveredRoot[];
  /** Space roots (transparent `name:` manifests) — for UI badges only. */
  readonly spaces: DiscoveredRoot[];
}

// `__stdlib__` is the reserved VFS mount for the bundled standard library
// (browser/Studio worlds). It is a set of dependency packages, never workspace
// packages, so it must never appear as a discovered root.
const PRUNE = new Set(["node_modules", ".git", "dist", "__stdlib__"]);

function stripTrailingSlash(uri: string): string {
  return uri.endsWith("/") ? uri.slice(0, -1) : uri;
}

/** Bare, NORMALIZED path for stable comparison — "/a/./b//../c/" → "a/c".
 *
 *  Trimming slashes alone was not enough: `ownerOf` matches by string prefix,
 *  so "/a/b/../m.arch" was attributed to package "a/b" — a directory that path
 *  leaves again — while the web twin (which routes every path through
 *  `vfsJoin`) answered "a". The two modules are required to agree. */
function normPath(p: string): string {
  const segs: string[] = [];
  for (const part of p.split("/")) {
    if (part === "" || part === ".") continue;
    if (part === "..") segs.pop();
    else segs.push(part);
  }
  return segs.join("/");
}

/**
 * Walk the tree under `rootUri` and report every package + space root.
 *
 * Unlike the loader's `collectArchFiles`, this descends through nested
 * `package:` boundaries — they are exactly what we want to find. Loose
 * `.arch` files not covered by any declared package root are attributed to a
 * synthesized implicit package at the tree root, so nothing is ever invisible.
 */
export async function discoverRoots(
  io: LspIO,
  rootUri: string
): Promise<DiscoveryResult> {
  const root = stripTrailingSlash(rootUri);
  const packages: DiscoveredRoot[] = [];
  const spaces: DiscoveredRoot[] = [];
  const archFiles: string[] = [];
  // package path → raw relative dependency paths from its manifest, resolved
  // to bare keys after the full set of packages is known.
  const rawDeps = new Map<string, string[]>();

  const stack: { dirUri: string; path: string }[] = [{ dirUri: root, path: "" }];
  // A directory URI is walked at most once. `joinUri(dir, "" + "/")` and
  // `joinUri(dir, "./")` both resolve back to a URI already on the stack, so a
  // VFS key with an empty or "." segment ("/svc//api/m.arch", which the drafts
  // route mints verbatim from a "…/drafts//svc/api/m.arch" PUT) made the walk
  // push the same directory forever — no result, no error, an OOM'd worker.
  const seen = new Set<string>([root]);
  while (stack.length > 0) {
    const { dirUri, path } = stack.pop()!;
    let entries: readonly { name: string; kind: "file" | "dir" }[];
    try {
      entries = await io.readDirectory(dirUri);
    } catch {
      continue;
    }

    if (entries.some((e) => e.name === "package.archspace" && e.kind === "file")) {
      const mUri = io.joinUri(dirUri, "package.archspace");
      const text = await io.readFile(mUri);
      const m = text !== null ? parsePackageManifest(text, mUri).manifest : undefined;
      if (m?.package !== undefined) {
        packages.push({ uri: dirUri, path, kind: "package", name: m.package });
        if (m.dependencies && m.dependencies.length > 0) {
          rawDeps.set(path, m.dependencies.map((d) => d.path));
        }
      } else if (m?.spaceName !== undefined) {
        spaces.push({ uri: dirUri, path, kind: "space", name: m.spaceName });
      }
    }

    for (const entry of entries) {
      if (PRUNE.has(entry.name)) continue;
      // An empty / "." / ".." entry name is a path artefact, not a directory
      // the user has: descending into it re-reads the SAME directory (the walk
      // never terminated) and, where it did terminate, reported the raw path
      // "svc/./api" as a package root the web twin called "svc/api".
      if (entry.name === "" || entry.name === "." || entry.name === "..") continue;
      const childPath = normPath(path === "" ? entry.name : `${path}/${entry.name}`);
      if (entry.kind === "dir") {
        const childUri = stripTrailingSlash(io.joinUri(dirUri, entry.name + "/"));
        if (seen.has(childUri)) continue;
        seen.add(childUri);
        stack.push({ dirUri: childUri, path: childPath });
      } else if (entry.name.endsWith(".arch") || /\.(md|markdown)$/i.test(entry.name)) {
        // Documents count toward ownership too, exactly as in viewer-core's twin
        // (`discover-roots.ts`): a docs-only orphan otherwise belonged to no
        // package on the web while the two sides are required to agree, and
        // `packages[0]` — the "" root sorts first — differed between them.
        archFiles.push(childPath);
      }
    }
  }

  // Implicit repo-root package: if the tree root is not itself a declared
  // package but loose `.arch` files exist outside every declared package
  // root, attribute them to an anonymous package at the root. Loading at the
  // root naturally excludes declared sub-packages (the loader stops at their
  // boundaries), so the implicit package collects only the orphans.
  const rootIsPackage = packages.some((p) => p.path === "");
  if (!rootIsPackage) {
    const declaredPaths = packages.map((p) => p.path).filter((p) => p !== "");
    const hasOrphan = archFiles.some(
      (f) => !declaredPaths.some((d) => f === d || f.startsWith(d + "/"))
    );
    if (hasOrphan) {
      packages.push({ uri: root, path: "", kind: "package", implicit: true });
    }
  }

  // Resolve each package's raw dependency paths to the bare keys of packages
  // found in this tree (external/stdlib deps that don't resolve are dropped).
  if (rawDeps.size > 0) {
    const known = new Set(packages.map((p) => p.path));
    for (const pkg of packages) {
      const raw = rawDeps.get(pkg.path);
      if (!raw) continue;
      const deps = raw
        .map((rel) => joinAndNormalize(pkg.path, rel))
        .filter((key) => known.has(key) && key !== pkg.path);
      if (deps.length > 0) (pkg as { deps?: readonly string[] }).deps = [...new Set(deps)];
    }
  }

  packages.sort((a, b) =>
    a.path === "" ? -1 : b.path === "" ? 1 : a.path.localeCompare(b.path)
  );
  spaces.sort((a, b) => a.path.localeCompare(b.path));

  return { packages, spaces };
}

/** Resolve a relative dep path against a package's bare path (handles ./ ..). */
function joinAndNormalize(base: string, rel: string): string {
  const segs = base === "" ? [] : base.split("/");
  // `rel` is walked RAW: it is relative to `base`, so its leading ".." must pop
  // a segment of the base. Pre-normalizing it in isolation swallowed those
  // (`"../shared"` → `"shared"`) and every declared dependency resolved one
  // level too deep, i.e. to nothing.
  for (const part of rel.split("/")) {
    if (part === "" || part === ".") continue;
    if (part === "..") segs.pop();
    else segs.push(part);
  }
  return segs.join("/");
}

/**
 * Resolve which package owns a given file path: the deepest package root that
 * is an ancestor of the path. Returns undefined only when `packages` is empty.
 * The root package (path "") matches any file, so a non-empty discovery result
 * with a root/implicit package always yields an owner.
 */
export function ownerOf(
  filePath: string,
  packages: readonly DiscoveredRoot[]
): DiscoveredRoot | undefined {
  const f = normPath(filePath);
  let best: DiscoveredRoot | undefined;
  for (const pkg of packages) {
    const p = normPath(pkg.path);
    const matches = p === "" || f === p || f.startsWith(p + "/");
    if (!matches) continue;
    if (best === undefined || p.length > normPath(best.path).length) best = pkg;
  }
  return best;
}
